import { type StoreCard, type StoresCarouselData } from '@/features/cms/components/CMS/types'
import { StoresCarouselRedesign } from '@/features/cms/components/CMS/components/BrandAmplification/StoresCarouselRedesign'
import { DynamicStoresCarousel } from '@/features/cms/components/CMS/components/BrandAmplification/DynamicStoresCarousel'
import { DetailedStoresCarousel } from '@/features/cms/components/CMS/components/BrandAmplification/DetailedStoresCarousel'

type RequiredFields = Required<Pick<StoreCard['data'], 'store_location_id' | 'metro_id'>>

export type StoreCardsRenderData = StoreCard['data'] & RequiredFields

const hasRequiredFields = (data: StoreCard['data']): data is StoreCardsRenderData =>
  !!data.store_location_id && !!data.metro_id

/**
 * This component corresponds to the `stores_carousel` content type in the CMS.
 *
 * - **Staging** - https://cms-staging.shipt.com/content-type/pool/stores_carousel
 * - **Production** - https://cms.shipt.com/content-type/pool/stores_carousel
 */
export const StoresCarousel = ({
  content_type_id,
  id,
  analytic_slug,
  data: {
    store_cards,
    heading,
    view_all_link,
    style_variant,
    seo_heading_level,
    subheading,
    background_color_hex,
    disclaimer,
    image,
    mobile_image,
    carousel_title,
  } = {
    seo_heading_level: 2,
  },
}: StoresCarouselData) => {
  if (!store_cards?.length) return null

  const storeCardsData = store_cards.map((card) => card.data).filter(hasRequiredFields)

  if (style_variant === 'detailed') {
    return (
      <DetailedStoresCarousel
        content_type_id={content_type_id}
        id={id}
        deliveryStoresList={storeCardsData}
        heading={heading}
        analyticSlug={analytic_slug}
        seoHeadLevel={seo_heading_level}
      />
    )
  }

  if (style_variant === 'dynamic') {
    return (
      <DynamicStoresCarousel
        content_type_id={content_type_id}
        id={id}
        deliveryStoresList={storeCardsData}
        heading={heading}
        view_all_link={view_all_link}
        analyticSlug={analytic_slug}
        seoHeadLevel={seo_heading_level}
        subheading={subheading}
        background_color_hex={background_color_hex}
        disclaimer={disclaimer}
        image={image}
        mobile_image={mobile_image}
        carousel_title={carousel_title}
      />
    )
  }

  return (
    <StoresCarouselRedesign
      content_type_id={content_type_id}
      id={id}
      deliveryStoresList={storeCardsData}
      heading={heading}
      view_all_link={view_all_link}
      analyticSlug={analytic_slug}
      seoHeadLevel={seo_heading_level}
      subheading={subheading}
      background_color_hex={background_color_hex}
    />
  )
}
